import { useBrands } from '@/hooks/useAccountBrands';
import { useInfluencers } from '@/hooks/useAccountInfluencers';
import { useKeywords } from '@/hooks/useAccountKeywords';
import { useCompetitorsGrouped, usePartnersGrouped } from '@/hooks/useDataSources';
import { useEvents } from '@/hooks/useEvents';
import { useMainChannelsGrouped } from '@/hooks/useMainChannel';
import { useProfile } from '@/hooks/useProfile';
import { useVersionTheme } from '@/hooks/useVersionTheme';
import { KeywordsSettings } from '@/pages/shared/settings';
import {
  Building2,
  Calendar,
  Hash,
  Home,
  Star,
  Tag,
} from 'lucide-react';
import React, { Suspense, useState } from 'react';

// Lazy load settings from shared directory
const EventsSettings = React.lazy(() => import('@/pages/shared/settings/EventsSettings'));
const InfluencersSettings = React.lazy(() => import('@/pages/shared/settings/InfluencersSettings'));
const MainChannelSettings = React.lazy(() => import('@/pages/shared/settings/MainChannelSettings'));
const BrandsSettings = React.lazy(() => import('@/pages/shared/settings/BrandsSettings'));
const DataSourcesSettings = React.lazy(() => import('@/pages/shared/settings/DataSourcesSettings'));

/**
 * Shared Settings Container
 *
 * Works in V11 using the version-aware theme hook.
 * All tabs use shared components that adapt to the current version's theme.
 */
const SettingsPage: React.FC = () => {
  const theme = useVersionTheme();

  const [activeTab, setActiveTab] = useState<
    'data-source' | 'main-channel' | 'influencers' | 'brands' | 'keywords' | 'events'
  >('main-channel');

  // Get user profile
  const { data: profile } = useProfile();
  const accountId = profile?.accountHealth?.accountId;

  // Fetch grouped datasources (counts datasources, not individual platforms)
  const { data: competitors = [] } = useCompetitorsGrouped(
    { account_id: accountId },
    { enabled: !!accountId }
  );

  // Calculate active competitors (count datasources, not platforms)
  const activeCompetitorsCount = competitors.filter(c => c.is_active).length;
  const totalCompetitorsCount = competitors.length;

  // Fetch partners data (grouped)
  const { data: partners = [] } = usePartnersGrouped(
    { account_id: accountId },
    { enabled: !!accountId }
  );

  // Calculate active partners (count datasources, not platforms)
  const activePartnersCount = partners.filter(p => p.is_active).length;
  const totalPartnersCount = partners.length;

  // Fetch main channels data (grouped)
  const { data: mainChannels = [] } = useMainChannelsGrouped(
    { account_id: accountId },
    { enabled: !!accountId }
  );

  // Calculate active main channels (count datasources, not platforms)
  const totalMainChannelsCount = mainChannels.length;

  // Fetch influencers data (grouped)
  const { data: influencers = [] } = useInfluencers(
    { account_id: accountId },
    { enabled: !!accountId }
  );

  // Calculate active influencers (count datasources, not platforms)
  const activeInfluencersCount = influencers.filter(i => i.is_active).length;
  const totalInfluencersCount = influencers.length;

  // Fetch brands data
  const { data: brands = [] } = useBrands({ account_id: accountId }, { enabled: !!accountId });

  // Calculate active brands
  const activeBrandsCount = brands.filter(b => b.is_active).length;
  const totalBrandsCount = brands.length;

  // Fetch keywords data
  const { data: keywords = [] } = useKeywords({ account_id: accountId }, { enabled: !!accountId });

  // Calculate active keywords
  const activeKeywordsCount = keywords.filter(k => k.is_active).length;
  const totalKeywordsCount = keywords.length;

  // Fetch events data
  const { data: events = [] } = useEvents({ account_id: accountId }, { enabled: !!accountId });

  // Calculate events (ongoing / all without past)
  const activeEventsCount = events.filter(e => e.status === 'ongoing').length;
  const totalEventsCount = events.filter(e => e.status !== 'past').length;

  const renderDataSource = () => (
    <Suspense
      fallback={
        <div className="space-y-4">
          <div className={`h-32 ${theme.surface} rounded-xl animate-pulse`} />
          <div className={`h-32 ${theme.surface} rounded-xl animate-pulse`} />
        </div>
      }
    >
      <DataSourcesSettings />
    </Suspense>
  );

  const renderKeywords = () => <KeywordsSettings />;

  const tabs = [
    { id: 'main-channel', label: 'Main Channel', icon: Home, count: '' },
    {
      id: 'data-source',
      label: 'Data Source',
      icon: Building2,
      count: `${activeCompetitorsCount + activePartnersCount} / ${totalCompetitorsCount + totalPartnersCount}`,
    },
    {
      id: 'influencers',
      label: 'Influencers',
      icon: Star,
      count: `${activeInfluencersCount} / ${totalInfluencersCount}`,
    },
    {
      id: 'brands',
      label: 'Brands',
      icon: Tag,
      count: `${activeBrandsCount} / ${totalBrandsCount}`,
    },
    {
      id: 'keywords',
      label: 'Keywords',
      icon: Hash,
      count: `${activeKeywordsCount} / ${totalKeywordsCount}`,
    },
    {
      id: 'events',
      label: 'Events',
      icon: Calendar,
      count: `${activeEventsCount} / ${totalEventsCount}`,
    },
  ];

  return (
    <div className={`min-h-screen ${theme.bg} ${theme.textPrimary} p-6`}>
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-3xl font-bold ${theme.textPrimary}`}>Account Settings</h1>
            <p className={`${theme.textSecondary} mt-2`}>
              Configure your monitoring targets and analysis sources
            </p>
          </div>
        </div>

        {/* Settings Tabs */}
        <div className={`border-b ${theme.border}`}>
          <nav className="flex space-x-8">
            {tabs
              .filter(tab => {
                // Hide keywords tab if environment variable is not set
                if (tab.id === 'keywords' && import.meta.env.VITE_SHOW_PROD !== 'true')
                  return false;
                return true;
              })
              .map(tab => {
                const Icon = tab.icon;
                return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-400'
                      : `border-transparent ${theme.textMuted} hover:${theme.textSecondary}`
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                  {tab.id !== 'main-channel' && (
                    <span className={`ml-1 py-0.5 px-2 rounded-full text-xs ${theme.surface}`}>
                      {tab.id === 'data-source' ||
                      tab.id === 'influencers' ||
                      tab.id === 'brands' ||
                      tab.id === 'keywords' ||
                      tab.id === 'events' ? (
                        <>
                          <span className="text-green-400">
                            {tab.id === 'data-source'
                              ? activeCompetitorsCount + activePartnersCount
                              : tab.id === 'influencers'
                                ? activeInfluencersCount
                                : tab.id === 'brands'
                                  ? activeBrandsCount
                                  : tab.id === 'keywords'
                                    ? activeKeywordsCount
                                    : activeEventsCount}
                          </span>
                          <span className={theme.textSecondary}>
                            {' '}
                            /{' '}
                            {tab.id === 'data-source'
                              ? totalCompetitorsCount + totalPartnersCount
                              : tab.id === 'influencers'
                                ? totalInfluencersCount
                                : tab.id === 'brands'
                                  ? totalBrandsCount
                                  : tab.id === 'keywords'
                                    ? totalKeywordsCount
                                    : totalEventsCount}
                          </span>
                        </>
                      ) : (
                        <span className={theme.textSecondary}>{tab.count}</span>
                      )}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="mt-8">
          {activeTab === 'main-channel' && (
            <Suspense
              fallback={
                <div className="space-y-4">
                  <div className={`h-32 ${theme.surface} rounded-xl animate-pulse`} />
                  <div className={`h-32 ${theme.surface} rounded-xl animate-pulse`} />
                </div>
              }
            >
              <MainChannelSettings />
            </Suspense>
          )}
          {activeTab === 'data-source' && renderDataSource()}
          {activeTab === 'influencers' && (
            <Suspense
              fallback={
                <div className="space-y-4">
                  <div className={`h-32 ${theme.surface} rounded-xl animate-pulse`} />
                  <div className={`h-32 ${theme.surface} rounded-xl animate-pulse`} />
                  <div className={`h-32 ${theme.surface} rounded-xl animate-pulse`} />
                </div>
              }
            >
              <InfluencersSettings />
            </Suspense>
          )}
          {activeTab === 'brands' && (
            <Suspense
              fallback={
                <div className="space-y-4">
                  <div className={`h-32 ${theme.surface} rounded-xl animate-pulse`} />
                  <div className={`h-32 ${theme.surface} rounded-xl animate-pulse`} />
                  <div className={`h-32 ${theme.surface} rounded-xl animate-pulse`} />
                </div>
              }
            >
              <BrandsSettings />
            </Suspense>
          )}
          {activeTab === 'keywords' && renderKeywords()}
          {activeTab === 'events' && (
            <Suspense
              fallback={
                <div className="space-y-4">
                  <div className={`h-32 ${theme.surface} rounded-xl animate-pulse`} />
                  <div className={`h-32 ${theme.surface} rounded-xl animate-pulse`} />
                  <div className={`h-32 ${theme.surface} rounded-xl animate-pulse`} />
                </div>
              }
            >
              <EventsSettings />
            </Suspense>
          )}
        </div>

        {/* Settings Summary */}
        <div className="flex flex-wrap justify-center gap-6 mt-12">
          <div className={`rounded-xl border ${theme.border} ${theme.surface} p-6 text-center min-w-[180px]`}>
            <Home className="h-8 w-8 text-blue-400 mx-auto mb-3" />
            <p className="text-2xl font-bold">
              <span className={theme.textPrimary}>{totalMainChannelsCount > 0 ? '✓' : '-'}</span>
            </p>
            <p className={`text-sm ${theme.textSecondary}`}>Main Channel</p>
          </div>
          <div className={`rounded-xl border ${theme.border} ${theme.surface} p-6 text-center min-w-[180px]`}>
            <Building2 className="h-8 w-8 text-blue-400 mx-auto mb-3" />
            <p className="text-2xl font-bold">
              <span className="text-green-400">{activeCompetitorsCount + activePartnersCount}</span>
              <span className={theme.textPrimary}> / {totalCompetitorsCount + totalPartnersCount}</span>
            </p>
            <p className={`text-sm ${theme.textSecondary}`}>Sources</p>
          </div>
          <div className={`rounded-xl border ${theme.border} ${theme.surface} p-6 text-center min-w-[180px]`}>
            <Star className="h-8 w-8 text-purple-400 mx-auto mb-3" />
            <p className="text-2xl font-bold">
              <span className="text-green-400">{activeInfluencersCount}</span>
              <span className={theme.textPrimary}> / {totalInfluencersCount}</span>
            </p>
            <p className={`text-sm ${theme.textSecondary}`}>Influencers</p>
          </div>
          <div className={`rounded-xl border ${theme.border} ${theme.surface} p-6 text-center min-w-[180px]`}>
            <Tag className="h-8 w-8 text-pink-400 mx-auto mb-3" />
            <p className="text-2xl font-bold">
              <span className="text-green-400">{activeBrandsCount}</span>
              <span className={theme.textPrimary}> / {totalBrandsCount}</span>
            </p>
            <p className={`text-sm ${theme.textSecondary}`}>Brands</p>
          </div>
          {import.meta.env.VITE_SHOW_PROD === 'true' && (
            <div className={`rounded-xl border ${theme.border} ${theme.surface} p-6 text-center min-w-[180px]`}>
              <Hash className="h-8 w-8 text-green-400 mx-auto mb-3" />
              <p className="text-2xl font-bold">
                <span className="text-green-400">{activeKeywordsCount}</span>
                <span className={theme.textPrimary}> / {totalKeywordsCount}</span>
              </p>
              <p className={`text-sm ${theme.textSecondary}`}>Keywords</p>
            </div>
          )}
          <div className={`rounded-xl border ${theme.border} ${theme.surface} p-6 text-center min-w-[180px]`}>
            <Calendar className="h-8 w-8 text-orange-400 mx-auto mb-3" />
            <p className="text-2xl font-bold">
              <span className="text-green-400">{activeEventsCount}</span>
              <span className={theme.textPrimary}> / {totalEventsCount}</span>
            </p>
            <p className={`text-sm ${theme.textSecondary}`}>Events (ongoing/all)</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
